import { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { downloadIaWorksheet, type ExportFormat } from "@/features/worksheets/api";
import { formatMoney } from "@/features/worksheets/money";
import type { IaWorksheetRow } from "@/features/worksheets/types";

export function IAWorksheetDialog({
  caseId,
  rows,
  open,
  onOpenChange,
}: {
  caseId: string;
  rows: IaWorksheetRow[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);

  async function download(fmt: ExportFormat) {
    setDownloading(fmt);
    try {
      await downloadIaWorksheet(caseId, fmt);
    } catch {
      toast.error("Couldn't download the IA worksheet — try again.");
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>IA Worksheet</DialogTitle>
          <DialogDescription>
            Per-tax-year installment breakdown. Download the full worksheet below.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tax year</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Cumulative</TableHead>
                <TableHead className="text-right">Months</TableHead>
                <TableHead className="text-right">Monthly</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-6 text-center">
                    No worksheet rows.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.taxYear}>
                    <TableCell>{row.taxYear}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(row.amount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(row.cumulative)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.months}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(row.monthly)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => download("pdf")}
            disabled={downloading !== null}
          >
            <FiDownload className="size-4" /> Download PDF
          </Button>
          <Button type="button" onClick={() => download("xlsx")} disabled={downloading !== null}>
            <FiDownload className="size-4" /> Download Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
